// Inserting toggles (`<details>`), and the blocks typed at the start of a line:
//   - `> ` makes a toggle (the quote syntax goes to the toggle, used more often)
//   - `| ` makes a quote (blockquote); StarterKit's `> ` gives way to the toggle
//
// StarterKit's blockquote `> ` rule stays (replacing it would need
// @tiptap/extension-blockquote as a direct dependency). This extension's `> `
// rule has a higher priority and runs first; only the first matching rule
// applies. Where a toggle cannot go (inside a list), it returns null and the
// default quote applies.

import {
  type Editor,
  Extension,
  InputRule,
  type JSONContent,
  type Range,
  wrappingInputRule,
} from "@tiptap/core";
import { TextSelection, type Transaction } from "@tiptap/pm/state";
import { m } from "@trameria/i18n";

/**
 * The placeholder title and body of a new toggle. Neither can be empty: the
 * canonical form needs a summary and a non-empty body (`detailsContent` is
 * `block+`), and the tokenizer reads an empty body as not canonical. A toggle
 * inserted empty would be saved but reload as an opaque HTML block, no longer
 * editable as a toggle. So it is inserted with placeholders and the title is
 * selected, to be typed over.
 */
export const toggleSummary = (): string =>
  m.editor_toggle_summary_placeholder();
export const toggleBody = (): string => m.editor_toggle_body_placeholder();

/** A new toggle node (canonical: a summary and a `block+` body). */
export function toggleContent(): JSONContent {
  return {
    type: "details",
    attrs: { open: true },
    content: [
      {
        type: "detailsSummary",
        content: [{ type: "text", text: toggleSummary() }],
      },
      {
        type: "detailsContent",
        content: [
          {
            type: "paragraph",
            content: [{ type: "text", text: toggleBody() }],
          },
        ],
      },
    ],
  };
}

/**
 * Selects the summary placeholder of the toggle at `detailsPos`, if it is
 * there. When the paragraph was empty `insertContent` replaced it, so the
 * toggle starts at the paragraph's position; when text was left after the
 * caret the paragraph was split and positions moved, so the toggle is checked first.
 */
export function selectToggleSummary(tr: Transaction, detailsPos: number): void {
  if (tr.doc.nodeAt(detailsPos)?.type.name !== "details") return;
  // details (+1) -> detailsSummary (+1): the start of the summary text.
  const from = detailsPos + 2;
  tr.setSelection(
    TextSelection.create(tr.doc, from, from + toggleSummary().length),
  );
}

/**
 * Inserts a toggle (slash menu; `range` covers "/" and the query). The
 * upstream `setDetails()` wraps the current block, which from an empty
 * paragraph gives a toggle whose body is one empty paragraph - the form that
 * reloads as an opaque block. The canonical node is built instead.
 */
export function insertToggle(editor: Editor, range: Range): void {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent(toggleContent())
    .command(({ tr }) => {
      selectToggleSummary(tr, range.from - 1);
      return true;
    })
    .run();
}

/** `> ` at the start of a line. */
const TOGGLE_RE = /^\s*>\s$/;
/** `| ` at the start of a line. */
const QUOTE_RE = /^\s*\|\s$/;

export const ToggleInput = Extension.create({
  name: "toggleInput",
  priority: 1000,

  addInputRules() {
    const { blockquote } = this.editor.schema.nodes;
    return [
      new InputRule({
        find: TOGGLE_RE,
        handler: ({ state, range, chain }) => {
          const $from = state.doc.resolve(range.from);
          // At the start of a paragraph, directly under the doc or in a toggle
          // body (where the slash menu works). Elsewhere the default quote applies.
          if (
            $from.parent.type.name !== "paragraph" ||
            $from.parentOffset !== 0
          )
            return null;
          const container = $from.depth >= 1 ? $from.node(-1) : null;
          if ($from.depth !== 1 && container?.type.name !== "detailsContent")
            return null;
          chain()
            .deleteRange(range)
            .insertContent(toggleContent())
            .command(({ tr }) => {
              selectToggleSummary(tr, range.from - 1);
              return true;
            })
            .run();
        },
      }),
      ...(blockquote
        ? [wrappingInputRule({ find: QUOTE_RE, type: blockquote })]
        : []),
    ];
  },
});
